// CapabilitiesProvider — new-arch replacement for the auth+capabilities
// slice of AuthContext.
//
// Fetches /api/v1/auth/me and /api/v1/agents in parallel, assembles the
// Capabilities view, and exposes it plus dev-persona switching to the tree.
// The agent catalog slice is handled by AgentCatalogProvider; this provider
// manages only the identity/entitlement surface.
//
// Security: no tokens are logged. The picker selection is persisted in
// sessionStorage; local-dev bearer tokens are re-derived from that selection
// on boot and stored through shared/auth.

/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import { log } from '@/shared/logger';
import { ApiError } from '@/shared/api';
import { fetchTestToken } from '@/shared/auth/bootstrap';
import { clearToken, setToken } from '@/shared/auth';
import { useAgentCatalog } from './AgentCatalogProvider';
import { useCurrentUserQuery, type CurrentUserView } from '@/entities/user';
import {
  ALLOWED_UPLOAD_EXTENSIONS as DEFAULT_ALLOWED_UPLOAD_EXTENSIONS,
  MAX_UPLOAD_BYTES as DEFAULT_MAX_UPLOAD_BYTES,
  MAX_UPLOAD_COUNT as DEFAULT_MAX_UPLOAD_COUNT,
} from '@/entities/file';

// ── Wire-format types (match the backend response shapes exactly) ────────────

export interface SkillInfo {
  name: string;
  description: string;
  allowed_groups: string[];
  required_tools: string[];
  required_mcps: string[];
  required_datasets: string[];
  example_prompts?: string[];
}

export interface MCPServerInfo {
  name: string;
  description: string;
  transport: string;
  allowed_groups: string[];
}

export interface DatasetInfo {
  name: string;
  description: string;
  path: string;
  allowed_groups: string[];
}

export interface DeniedCapability {
  name: string;
  type: string;
  description: string;
  required_groups: string[];
}

export interface TimeoutSettings {
  ui_stream_timeout_seconds: number;
  backend_stream_timeout_seconds: number;
  llm_idle_timeout_seconds: number;
}

/**
 * Chat attachment limits — sourced from the backend's
 * services/artifacts.py constants via /auth/me. The single source of
 * truth; do not hand-maintain a separate frontend allowlist.
 */
export interface UploadLimits {
  allowed_extensions: string[];
  max_count: number;
  max_bytes: number;
}

export interface Capabilities {
  /** External Okta sub. Stable, but NOT the internal users.id UUID. */
  user_id: string;
  /** Internal users.id resolved from user_id. Null until the user row exists. */
  internal_user_id: string | null;
  email: string;
  display_name?: string;
  groups: string[];
  auth_method: string;
  is_admin: boolean;
  is_real_admin: boolean;
  /** Agents the caller can CHAT with (group-based access). */
  allowed_agents: string[];
  /** Agents the caller explicitly owns. */
  owned_agents: string[];
  /**
   * Legacy rollout setting retained in the auth payload for compatibility.
   * Legacy rollout value; the Skill Registry route is no longer hidden by it.
   */
  skills_hub_enabled: boolean;
  skills: SkillInfo[];
  mcp_servers: MCPServerInfo[];
  datasets: DatasetInfo[];
  denied: DeniedCapability[];
  timeouts?: TimeoutSettings;
  upload_limits: UploadLimits;
  pending_iam_groups?: string[];
  session_expires_at?: number;
  auth_session_id?: string;
  auth_session_started_at?: string;
}

// ── Internal helpers ─────────────────────────────────────────────────────────

const DEFAULT_STREAM_TIMEOUT_MS = 3_600_000;

function buildMcpServers(agents: ReadonlyArray<{ mcps?: ReadonlyArray<string> }>): MCPServerInfo[] {
  const mcpMap = new Map<string, MCPServerInfo>();
  for (const agent of agents) {
    for (const mcpId of agent.mcps ?? []) {
      if (!mcpMap.has(mcpId)) {
        mcpMap.set(mcpId, {
          name: mcpId,
          description: `MCP server: ${mcpId}`,
          transport: 'http',
          allowed_groups: ['*'],
        });
      }
    }
  }
  return Array.from(mcpMap.values());
}

function buildCapabilities(currentUser: CurrentUserView): Capabilities {
  const resolvedUiTimeoutSeconds = Number(currentUser.timeouts?.uiStreamTimeoutSeconds);
  const timeouts: TimeoutSettings = {
    ui_stream_timeout_seconds:
      Number.isFinite(resolvedUiTimeoutSeconds) && resolvedUiTimeoutSeconds > 0
        ? resolvedUiTimeoutSeconds
        : Math.floor(DEFAULT_STREAM_TIMEOUT_MS / 1000),
    backend_stream_timeout_seconds: Number(currentUser.timeouts?.backendStreamTimeoutSeconds) || 3600,
    llm_idle_timeout_seconds: Number(currentUser.timeouts?.llmIdleTimeoutSeconds) || 3600,
  };

  const uploadLimits: UploadLimits = {
    allowed_extensions: Array.isArray(currentUser.uploadLimits?.allowedExtensions)
      ? [...currentUser.uploadLimits.allowedExtensions]
      : [...DEFAULT_ALLOWED_UPLOAD_EXTENSIONS],
    max_count: Number(currentUser.uploadLimits?.maxCount) || DEFAULT_MAX_UPLOAD_COUNT,
    max_bytes: Number(currentUser.uploadLimits?.maxBytes) || DEFAULT_MAX_UPLOAD_BYTES,
  };

  return {
    user_id: currentUser.id,
    internal_user_id: currentUser.internalUserId,
    email: currentUser.email,
    display_name: currentUser.name || undefined,
    groups: [...currentUser.groups],
    auth_method: import.meta.env.DEV ? 'dev_token' : 'okta',
    is_admin: currentUser.isAdmin,
    is_real_admin: currentUser.isRealAdmin,
    allowed_agents: [...currentUser.allowedAgents],
    owned_agents: [...currentUser.ownedAgents],
    skills_hub_enabled: currentUser.skillsHubEnabled,
    skills: [],
    mcp_servers: [],
    datasets: [],
    denied: [],
    timeouts,
    upload_limits: uploadLimits,
    pending_iam_groups: [...currentUser.pendingIamGroups],
    session_expires_at: currentUser.sessionExpiresAt,
    auth_session_id: currentUser.authSessionId,
    auth_session_started_at: currentUser.authSessionStartedAt,
  };
}

// ── Context and provider ─────────────────────────────────────────────────────

export interface CapabilitiesContextValue {
  capabilities: Capabilities | null;
  loading: boolean;
  status: 'loading' | 'ready' | 'unauthenticated' | 'error';
  canSwitchDevPersona: boolean;
  devPersona: string;
  switchDevPersona: (persona: string) => Promise<void>;
  refreshCapabilities: () => Promise<void>;
}

const CapabilitiesContext = createContext<CapabilitiesContextValue | null>(null);

const DEV_PERSONA_STORAGE_KEY = 'agent-hub-dev-persona';
const DEFAULT_DEV_PERSONA = 'default';
const CAN_SWITCH_DEV_PERSONA = import.meta.env.DEV;

function readStoredDevPersona(): string {
  try {
    const stored = sessionStorage.getItem(DEV_PERSONA_STORAGE_KEY);
    if (!stored || stored === 'admin' || stored === DEFAULT_DEV_PERSONA) {
      return DEFAULT_DEV_PERSONA;
    }
    return stored;
  } catch {
    return DEFAULT_DEV_PERSONA;
  }
}

export function CapabilitiesProvider({ children }: { children: ReactNode }) {
  const [devPersona, setDevPersona] = useState(readStoredDevPersona);
  const [bootstrapReady, setBootstrapReady] = useState(!CAN_SWITCH_DEV_PERSONA);
  const { agents } = useAgentCatalog();
  const currentUser = useCurrentUserQuery(bootstrapReady);

  const refreshCapabilities = useCallback(async () => {
    await currentUser.refetch();
  }, [currentUser]);

  const syncTokenToDevPersona = useCallback(async () => {
    if (!CAN_SWITCH_DEV_PERSONA) return;
    const storedPersona = readStoredDevPersona();
    if (storedPersona === DEFAULT_DEV_PERSONA) {
      clearToken();
      try {
        sessionStorage.removeItem(DEV_PERSONA_STORAGE_KEY);
      } catch {
        // ignore storage access failures
      }
      setDevPersona(DEFAULT_DEV_PERSONA);
      return;
    }

    try {
      const { token: nextToken } = await fetchTestToken(storedPersona);
      setToken(nextToken);
      setDevPersona(storedPersona);
    } catch (err) {
      clearToken();
      try {
        sessionStorage.removeItem(DEV_PERSONA_STORAGE_KEY);
      } catch {
        // ignore storage access failures
      }
      setDevPersona(DEFAULT_DEV_PERSONA);
      log.error('capabilities_dev_persona_bootstrap_failed', err, {
        persona: storedPersona,
      });
    }
  }, []);

  const switchDevPersona = useCallback(
    async (persona: string) => {
      if (!CAN_SWITCH_DEV_PERSONA) return;
      if (persona === DEFAULT_DEV_PERSONA) {
        clearToken();
        sessionStorage.removeItem(DEV_PERSONA_STORAGE_KEY);
        setDevPersona(DEFAULT_DEV_PERSONA);
        await refreshCapabilities();
        return;
      }
      const { token: nextToken } = await fetchTestToken(persona);
      setToken(nextToken);
      sessionStorage.setItem(DEV_PERSONA_STORAGE_KEY, persona);
      setDevPersona(persona);
      await refreshCapabilities();
    },
    [refreshCapabilities],
  );

  useEffect(() => {
    void (async () => {
      await syncTokenToDevPersona();
      setBootstrapReady(true);
    })();
  }, [syncTokenToDevPersona]);

  useEffect(() => {
    if (!currentUser.error || currentUser.error instanceof ApiError) return;
    log.error('capabilities_load_failed', currentUser.error);
  }, [currentUser.error]);

  const capabilities = useMemo(
    () => (currentUser.data ? { ...buildCapabilities(currentUser.data), mcp_servers: buildMcpServers(agents) } : null),
    [agents, currentUser.data],
  );
  const loading = !bootstrapReady || currentUser.isLoading;
  const authStatus = currentUser.error instanceof ApiError ? currentUser.error.context?.status : undefined;
  const status: CapabilitiesContextValue['status'] = loading
    ? 'loading'
    : authStatus === 401 || authStatus === 403
      ? 'unauthenticated'
      : currentUser.isError
        ? 'error'
        : capabilities === null
          ? 'unauthenticated'
          : 'ready';

  const value: CapabilitiesContextValue = {
    capabilities,
    loading,
    status,
    canSwitchDevPersona: CAN_SWITCH_DEV_PERSONA,
    devPersona,
    switchDevPersona,
    refreshCapabilities,
  };

  return (
    <CapabilitiesContext.Provider value={value}>
      {children}
    </CapabilitiesContext.Provider>
  );
}

export function useCapabilities(): CapabilitiesContextValue {
  const ctx = useContext(CapabilitiesContext);
  if (!ctx) throw new Error('useCapabilities must be inside CapabilitiesProvider');
  return ctx;
}
